import { View, Text, Image, Touchable, UserDetailsModal, MemberOptionsModal } from '@/components'
import { textVariant, theme } from '@/config'
import { createStyles } from '@codeleap/styles'
import { TypeGuards } from '@codeleap/types'
import { memo, useMemo, useCallback } from 'react'
import { useMessageContext, useChannelStateContext } from 'stream-chat-react'
import { MessageProps } from '../types'
import { ChatMember } from '@packages/chat'
import { Getstream } from '@/services'

type MessageAvatarProps = { user: MessageProps['message']['user'] }

const MessageAvatarWithContext = ({ user }: MessageAvatarProps) => {
  const name = user?.name
  const image = user?.image
  const { channel } = useChannelStateContext()
  const { isGroup, isAdmin } = Getstream.Chat.useMyMembership(channel)

  const { initials, randomColor } = useMemo(() => {
    const [first = ''] = TypeGuards.isString(name) ? name.split(' ') : ['']
    const initials = [first[0]]

    return {
      initials: initials.join(' '),
      randomColor: theme.baseColors.secondarySolid500,
    }
  }, [name])

  const handleAvatarPress = useCallback(() => {
    if (!isGroup) {
      UserDetailsModal.open({ user })
      return
    }

    const memberToShow = ({
      user: user,
      user_id: user?.id,
    } as ChatMember)

    MemberOptionsModal.open({
      member: memberToShow,
      isAdmin,
      channel,
    })
  }, [isGroup, user, isAdmin])

  return (
    <Touchable debugName='pressAvatar' onPress={handleAvatarPress} style={['mt:auto']}>
      <View style={[styles.wrapper, { backgroundColor: !!image ? 'transparent' : randomColor }]}>
        {!image ? (
          <Text style={styles.initials} text={initials} />
        ) : (
          <Image objectFit='cover' style={styles.image} source={image as any} alt='chat:avatar' />
        )}
      </View>
    </Touchable>
  )
}

const styles = createStyles(theme => ({
  wrapper: {
    ...theme.presets.center,
    borderRadius: theme.radius.infinite,
    width: theme.size.largeish,
    height: theme.size.largeish,
    minWidth: theme.size.largeish,
    maxWidth: theme.size.largeish,
    minHeight: theme.size.largeish,
    maxHeight: theme.size.largeish,
    overflow: 'hidden',
  },
  initials: {
    ...textVariant(['p3', 'semiBold']),
    color: theme.baseColors.primarySolid50,
  },
  image: {
    width: theme.size.largeish,
    height: theme.size.largeish,
  },
}))

function areEqual(
  prevProps: MessageAvatarProps,
  nextProps: MessageAvatarProps,
) {
  if (prevProps?.user?.name && nextProps?.user?.name) return false
  if (prevProps?.user?.image && nextProps?.user?.image) return false
  return true

}

const MemoizedMessageAvatar = memo(MessageAvatarWithContext, areEqual)

export const MessageAvatar = () => {
  const props = useMessageContext()
  return <MemoizedMessageAvatar user={props.message.user} />
}
